import { Controller } from "@hotwired/stimulus"

class AlreadyGuessed extends Error {
  constructor() {
    super("already guessed")
    this.name = "AlreadyGuessed"
    this.message = "You've already guessed that"
  }
}

class MissingRequired extends Error {
  constructor(letters) {
    super("missing required letters")
    this.name = "MissingRequired"
    this.letters = letters
    this.message = "Must include " + letters.join(", ")
  }
}

class InvalidWord extends Error {
  constructor() {
    super("invalid word")
    this.name = "InvalidWord"
    this.message = "Invalid word"
  }
}

class IncompleteWord extends Error {
  constructor() {
    super("incomplete word")
    this.name = "IncompleteWord"
    this.message = "Incomplete word, enter more letters."
  }
}

export default class extends Controller {
  static targets = ["guess"]

  // Called by GameController after wordlist loads
  setup(taboo) {
    this.taboo = taboo
    this.locked = new Array(taboo.length).fill(null)
    this.hooked = {}

    this.element.innerHTML = ''
    this.appendNewRow(false)
  }

  currentWordArray() {
    const cells = this.element.querySelectorAll('li:last-of-type ul li:not(:empty)')
    return Array.from(cells).map(c => c.innerHTML)
  }

  get currentRow() {
    return this.element.querySelector(':scope > li:last-of-type')
  }

  // Throws on validation failure (exception-based flow)
  validate(wordArray, wordlist, previousGuesses) {
    if (wordArray.length !== this.taboo.length) { throw new IncompleteWord() }

    const missingLocked = []
    for (let i = 0; i < this.locked.length; i++) {
      if (this.locked[i] != null && wordArray[i] !== this.locked[i]) {
        missingLocked.push(this.locked[i])
      }
    }

    const missingHooked = []
    for (const [letter, requiredCount] of Object.entries(this.hooked)) {
      const actualCount = wordArray.filter(l => l === letter).length
      if (actualCount < requiredCount) {
        for (let i = 0; i < requiredCount - actualCount; i++) {
          missingHooked.push(letter)
        }
      }
    }

    const allMissing = [...missingLocked, ...missingHooked]
    if (allMissing.length > 0) { throw new MissingRequired(allMissing) }

    const word = wordArray.join('')
    if (previousGuesses.includes(word)) { throw new AlreadyGuessed() }
    if (!wordlist.includes(word)) { throw new InvalidWord() }
  }

  classify(wordArray, taboo) {
    const tabooLetters = taboo.split('')
    const result = { letters: [...wordArray], statuses: new Array(wordArray.length).fill('normal') }

    const available = {}
    tabooLetters.forEach(l => { available[l] = (available[l] || 0) + 1 })

    for (let i = 0; i < wordArray.length; i++) {
      if (wordArray[i] === tabooLetters[i]) {
        result.statuses[i] = 'locked'
        available[wordArray[i]]--
      }
    }

    for (let i = 0; i < wordArray.length; i++) {
      if (result.statuses[i] === 'locked') continue
      if (available[wordArray[i]] && available[wordArray[i]] > 0) {
        result.statuses[i] = 'hooked'
        available[wordArray[i]]--
      }
    }

    return result
  }

  // Full accept flow: update locks/hooks, color row, annotate score, append new row
  acceptGuess(wordArray, points, taboo) {
    this.updateLocksAndHooks(wordArray, taboo)
    this.colorCurrentRow(wordArray, taboo)
    this.annotateScore(this.currentRow, points)
    this.appendNewRow()
  }

  updateLocksAndHooks(wordArray, taboo) {
    const classification = this.classify(wordArray, taboo)

    for (let i = 0; i < classification.letters.length; i++) {
      if (classification.statuses[i] === 'locked') {
        this.locked[i] = classification.letters[i]
      }
    }

    const revealedCounts = {}

    for (let i = 0; i < classification.letters.length; i++) {
      if (classification.statuses[i] === 'locked' || classification.statuses[i] === 'hooked') {
        revealedCounts[classification.letters[i]] = (revealedCounts[classification.letters[i]] || 0) + 1
      }
    }

    for (const [letter, count] of Object.entries(revealedCounts)) {
      const currentReq = this.hooked[letter] || 0
      if (count > currentReq) {
        this.hooked[letter] = count
      }
    }
  }

  colorCurrentRow(wordArray, taboo) {
    const cells = this.element.querySelectorAll('li:last-of-type ul li')
    const classification = this.classify(wordArray, taboo)

    for (let i = 0; i < cells.length; i++) {
      cells[i].classList.remove('locked', 'hooked', 'normal')
      cells[i].classList.add(classification.statuses[i])
    }
  }

  annotateScore(row, points) {
    const span = row.querySelector('.guess-score')
    span.textContent = '+' + points
  }

  appendNewRow(animate = true) {
    const guess = document.createElement('li')
    const word = document.createElement('ul')

    for (let i = 0; i < this.taboo.length; i++) {
      const letter = document.createElement('li')
      if (this.locked[i] != null) {
        letter.innerHTML = this.locked[i]
        letter.classList.add('locked')
      }
      word.append(letter)
    }

    guess.dataset.guessesTarget = "guess"
    guess.appendChild(word)

    const scoreSpan = document.createElement('span')
    scoreSpan.classList.add('guess-score')
    scoreSpan.innerHTML = '&nbsp;'
    guess.appendChild(scoreSpan)

    if (animate) {
      guess.classList.add('slide-in')
      guess.addEventListener('animationend', () => {
        guess.classList.remove('slide-in')
      }, { once: true })
    }

    this.element.appendChild(guess)
  }

  clearError() {
    const row = this.currentRow
    if (row) row.classList.remove('error', 'shake')
  }

  markError() {
    const row = this.currentRow
    row.classList.add('shake', 'error')
    setTimeout(() => { row.classList.remove('shake') }, 350)
  }

  markCorrect() {
    const row = this.currentRow
    row.querySelectorAll('ul li').forEach(cell => {
      cell.classList.remove('locked', 'hooked')
    })
    row.classList.add('glow', 'guessed')
  }

  restoreGuess(wordArray, points, taboo) {
    const cells = this.currentRow.querySelectorAll('ul li')
    for (let i = 0; i < wordArray.length; i++) {
      cells[i].textContent = wordArray[i]
    }
    this.updateLocksAndHooks(wordArray, taboo)
    this.colorCurrentRow(wordArray, taboo)
    this.annotateScore(this.currentRow, points)
    this.appendNewRow(false)
  }

  revealLetter(position, letter) {
    const cells = this.element.querySelectorAll('li:last-of-type ul li')
    cells.forEach(cell => { if (!cell.classList.contains('locked')) cell.textContent = '' })
    if (cells[position]) {
      cells[position].textContent = letter
      cells[position].classList.add('locked')
    }
  }

  revealTaboo(taboo) {
    const row = this.currentRow
    let letters = taboo.split('')
    row.querySelectorAll('ul li').forEach(cell => {
      cell.innerHTML = letters.shift()
    })

    this.markCorrect()
  }
}
